import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    OnInit,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { TuiIcon } from '@taiga-ui/core';
import { AuthService } from '../../../core/services/auth.service';
import { LaboratoriosService } from '../../../features/usuarios/services/laboratorios.service';
import { LaboratoryOption } from '../../../features/usuarios/models/laboratorio.model';

/**
 * Laboratorios asignados al usuario, en la cabecera.
 *
 * Sirve para saber de un vistazo sobre qué datos se está trabajando. No se
 * muestra a quien administra: tiene todos los laboratorios y la etiqueta no le
 * aportaría nada, solo ruido.
 *
 * Cuando son muchos se resumen en un contador y el listado completo se
 * despliega al pulsar, para no empujar el resto de la cabecera.
 */
@Component({
    selector: 'app-lab-summary',
    standalone: true,
    imports: [TuiIcon],
    templateUrl: './lab-summary.component.html',
    styleUrl: './lab-summary.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LabSummaryComponent implements OnInit {
    /** Nombres que se escriben antes de pasar a «N laboratorios». */
    private static readonly MAX_NOMBRES = 2;

    private readonly destroyRef = inject(DestroyRef);
    private readonly authService = inject(AuthService);
    private readonly laboratoriosService = inject(LaboratoriosService);

    private readonly labs = signal<readonly LaboratoryOption[]>([]);
    private readonly loaded = signal(false);

    readonly open = signal(false);

    readonly total = computed(() => this.labs().length);

    /** Solo se muestra a quien tiene una asignación concreta. */
    readonly visible = computed(() => this.loaded() && this.total() > 0);

    /** Con pocos se escriben los nombres; con muchos, la cuenta. */
    readonly resumen = computed(() => {
        const labs = this.labs();

        if (labs.length <= LabSummaryComponent.MAX_NOMBRES) {
            return labs.map((lab) => lab.itmsGrpNam || lab.itmsGrpCod).join(' · ');
        }

        return `${labs.length} laboratorios`;
    });

    readonly desplegable = computed(() => this.total() > LabSummaryComponent.MAX_NOMBRES);

    readonly listado = computed(() =>
        [...this.labs()].sort((a, b) =>
            (a.itmsGrpNam || a.itmsGrpCod).localeCompare(b.itmsGrpNam || b.itmsGrpCod, 'es'),
        ),
    );

    ngOnInit(): void {
        // A quien administra no se le consulta siquiera: los tiene todos.
        if (this.authService.isRbiAdmin() || this.authService.isSuperUser()) {
            this.loaded.set(true);
            return;
        }

        this.laboratoriosService
            .getMyLaboratories()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.loaded.set(true)),
            )
            .subscribe({
                next: (labs) => this.labs.set(labs),
                // Es informativo: si falla, simplemente no se muestra nada.
                error: () => this.labs.set([]),
            });
    }

    toggle(): void {
        if (this.desplegable()) {
            this.open.update((abierto) => !abierto);
        }
    }

    close(): void {
        this.open.set(false);
    }
}
